import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Target, 
  PlayCircle, 
  Clock, 
  BookOpen, 
  Star,
  TrendingUp,
  Info,
  ChevronRight,
  Lightbulb,
  CheckCircle,
  BarChart3,
  User,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useStartHereRecommendations } from '@/hooks/useStartHereRecommendations';
import { useToast } from '@/hooks/use-toast';

interface StartHereGuidanceProps {
  disciplineId: string;
  disciplineName: string;
  onRecommendationSelect?: (nodeId: string) => void;
  showUserProfile?: boolean;
  compact?: boolean;
}

export const StartHereGuidance: React.FC<StartHereGuidanceProps> = ({
  disciplineId,
  disciplineName,
  onRecommendationSelect,
  showUserProfile = true,
  compact = false
}) => {
  const [selectedRecommendation, setSelectedRecommendation] = useState<string | null>(null);
  const { toast } = useToast();
  
  const { 
    data: analysis, 
    isLoading, 
    error 
  } = useStartHereRecommendations(disciplineId);

  const handleStartRecommendation = (nodeId: string, title: string) => {
    setSelectedRecommendation(nodeId);
    
    if (onRecommendationSelect) {
      onRecommendationSelect(nodeId);
    } else {
      // Default behavior: navigate to content
      window.location.href = `/content/view/${nodeId}`;
    }
    
    toast({
      title: 'Starting Your Learning Journey',
      description: `Beginning with "${title}" - great choice!`,
    });
  };

  const getDifficultyColor = (level: number) => {
    switch (level) {
      case 1: return 'bg-white text-green-800';
      case 2: return 'bg-white text-black';
      case 3: return 'bg-white text-black';
      case 4: return 'bg-white text-black';
      case 5: return 'bg-red-100 text-red-800';
      default: return 'bg-white text-black';
    }
  };

  const getDifficultyLabel = (level: number) => {
    const labels = ['', 'Foundational', 'Intermediate', 'Advanced', 'Research', 'Expert'];
    return labels[level] || 'Intermediate';
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-black';
    if (score >= 40) return 'text-black';
    return 'text-black';
  };

  if (isLoading) {
    return (
      <Card className={compact ? 'p-4' : ''}>
        <CardHeader className={compact ? 'pb-3' : ''}>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-6 w-48" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-20 w-full rounded-lg" />
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Unable to generate personalized recommendations at this time. 
          Please try exploring the content below or refresh the page.
        </AlertDescription>
      </Alert>
    );
  }

  if (!analysis || analysis.recommendations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <CardTitle className="text-lg">All Caught Up!</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-black">
            You've completed all available content in {disciplineName}. 
            Check back later for new content, or explore related disciplines!
          </p>
        </CardContent>
      </Card>
    );
  }

  const { recommendations, userProfile, disciplineInfo } = analysis;
  const topRecommendation = recommendations[0];
  const alternativeRecommendations = recommendations.slice(1);

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white rounded-lg">
            <Target className="h-6 w-6 text-black" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Start Here</h2>
            <p className="text-black">
              Personalized entry points for {disciplineName}
            </p>
          </div>
        </div>
        
        {showUserProfile && (
          <div className="text-right">
            <div className="text-sm text-black">Your Level</div>
            <Badge variant="outline" className="font-medium">
              {getDifficultyLabel(userProfile.preferredDifficulty)}
            </Badge>
          </div>
        )}
      </div>

      {/* Primary Recommendation */}
      <Card className="border-blue-200 bg-white/30">
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Star className="h-4 w-4 text-sky-500 fill-current" />
                <CardTitle className="text-lg">Best Match for You</CardTitle>
                <Badge className={getConfidenceColor(topRecommendation.confidenceScore)}>
                  {topRecommendation.confidenceScore}% match
                </Badge>
              </div>
              <CardDescription className="text-base">
                Our AI analysis suggests this as your optimal starting point
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Recommendation Card */}
          <div className="bg-white rounded-lg p-4 border border-blue-200">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-1">{topRecommendation.title}</h3>
                <p className="text-black text-sm line-clamp-2">
                  {topRecommendation.description}
                </p>
              </div>
            </div>
            
            {/* Metadata */}
            <div className="flex items-center gap-4 mb-3 text-sm">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3 text-black" />
                <span>{topRecommendation.estimatedTime} min</span>
              </div>
              <Badge className={getDifficultyColor(topRecommendation.difficultyLevel)} variant="outline">
                {getDifficultyLabel(topRecommendation.difficultyLevel)}
              </Badge>
              <Badge variant="secondary" className="text-xs">
                {topRecommendation.contentType}
              </Badge>
            </div>
            
            {/* Rationale */}
            <div className="bg-white rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-4 w-4 text-black mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm font-medium text-blue-900 mb-1">Why this recommendation?</div>
                  <div className="text-sm text-black">{topRecommendation.rationale}</div>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <Button 
              className="w-full" 
              onClick={() => handleStartRecommendation(topRecommendation.nodeId, topRecommendation.title)}
              disabled={selectedRecommendation === topRecommendation.nodeId}
            >
              {selectedRecommendation === topRecommendation.nodeId ? (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayCircle className="h-4 w-4 mr-2" />
                  Start Learning
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Alternative Recommendations */}
      {alternativeRecommendations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <TrendingUp className="h-5 w-5" />
              Alternative Starting Points
            </CardTitle>
            <CardDescription>
              Other excellent options based on your learning profile
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {alternativeRecommendations.map((recommendation, index) => (
              <div 
                key={recommendation.nodeId}
                className="border rounded-lg p-4 hover:bg-white transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{recommendation.title}</h4>
                      <Badge variant="outline" className={getConfidenceColor(recommendation.confidenceScore)}>
                        {recommendation.confidenceScore}% match
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-black mb-2 line-clamp-1">
                      {recommendation.description}
                    </p>
                    
                    <div className="flex items-center gap-3 text-xs text-black mb-2">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {recommendation.estimatedTime} min
                      </span>
                      <Badge className={getDifficultyColor(recommendation.difficultyLevel)} variant="outline" size="sm">
                        {getDifficultyLabel(recommendation.difficultyLevel)}
                      </Badge>
                    </div>
                    
                    <div className="text-xs text-black">
                      {recommendation.rationale}
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleStartRecommendation(recommendation.nodeId, recommendation.title)}
                    disabled={selectedRecommendation === recommendation.nodeId}
                  >
                    {selectedRecommendation === recommendation.nodeId ? (
                      <CheckCircle className="h-3 w-3" />
                    ) : (
                      <ArrowRight className="h-3 w-3" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* User Profile Summary (if enabled) */}
      {showUserProfile && !compact && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5" />
              Your Learning Profile
            </CardTitle>
            <CardDescription>
              How we personalized these recommendations
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {userProfile.completedNodes}
                </div>
                <div className="text-xs text-black">
                  Completed in {disciplineName}
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {getDifficultyLabel(userProfile.preferredDifficulty)}
                </div>
                <div className="text-xs text-black">
                  Preferred Difficulty
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {userProfile.studyTimeAvailability}m
                </div>
                <div className="text-xs text-black">
                  Study Session Length
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {userProfile.learningStyle}
                </div>
                <div className="text-xs text-black">
                  Learning Style
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Discipline Overview */}
      {!compact && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="h-5 w-5" />
              {disciplineName} Overview
            </CardTitle>
            <CardDescription>
              Discipline structure and learning opportunities
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {disciplineInfo.totalNodes}
                </div>
                <div className="text-xs text-black">
                  Total Content Pieces
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {disciplineInfo.entryPoints}
                </div>
                <div className="text-xs text-black">
                  Entry Points Available
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {Math.round(disciplineInfo.avgStudyTime)}m
                </div>
                <div className="text-xs text-black">
                  Avg Study Time
                </div>
              </div>
              
              <div className="text-center">
                <div className="text-2xl font-bold text-black">
                  {disciplineInfo.difficultyRange[0]}-{disciplineInfo.difficultyRange[1]}
                </div>
                <div className="text-xs text-black">
                  Difficulty Range
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 